import type { DBSchema } from 'idb';
import { openDB } from 'idb';
import { useCallback } from 'react';

interface DraftDB<T> extends DBSchema {
  drafts: {
    key: string;
    value: {
      data: T;
      updatedAt: number;
    };
  };
}

type UseIndexedDbDraftOptions = {
  key: string;
  dbName?: string;
  version?: number;
};

export type IndexedDbDraftApi<T extends Record<string, any>> = {
  save: (data: T) => Promise<void>;
  saveField: <K extends keyof T>(field: K, value: T[K]) => Promise<void>;
  restore: () => Promise<T | null>;
  clear: () => Promise<void>;
};

export function useIndexedDbDraft<T>({ key, dbName = 'client_edit_drafts', version = 1 }: UseIndexedDbDraftOptions) {
  const getDb = useCallback(() => {
    return openDB<DraftDB<T>>(dbName || '', version, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('drafts')) {
          db.createObjectStore('drafts');
        }
      },
    });
  }, [dbName, version]);

  const save = useCallback(
    async (data: T) => {
      const db = await getDb();
      await db.put('drafts', { data, updatedAt: Date.now() }, key);
    },
    [getDb, key]
  );

  const restore = useCallback(async (): Promise<T | null> => {
    const db = await getDb();
    const record = await db.get('drafts', key);
    return record?.data ?? null;
  }, [getDb, key]);

  const clear = useCallback(async () => {
    const db = await getDb();
    await db.delete('drafts', key);
  }, [getDb, key]);

  const saveField = useCallback(
    async <K extends keyof T>(field: K, value: T[K]) => {
      const db = await getDb();
      const existing = await db.get('drafts', key);

      const nextData = {
        ...(existing?.data ?? {}),
        [field]: value,
      } as T;

      await db.put('drafts', { data: nextData, updatedAt: Date.now() }, key);
    },
    [getDb, key]
  );

  return {
    save,
    restore,
    clear,
    saveField,
  };
}
